import { useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet } from '../../api/axios-handler';
import useAuth from '../utils/useAuth';
import { usePostCallPolling } from './usePostCallPolling';

export const FI_SEARCH_PROGRESS_EVENT = 'fi-search-progress' as const;

export interface FiSearchProgressPayload {
  context: 'fi_search_progress';
  key: string;
  message: string;
  tool?: string;
  query?: string;
}

interface Notification {
  context: 'engagement_check_in' | 'updated_call' | 'updated_offers' | 'call_passed' | 'fi_search_progress';
  request_id?: number;
  key?: string;
  message?: string;
  tool?: string;
  query?: string;
}

const fetchNotifications = async (userId?: number) => {
  if (userId) {
    return await axiosGet(`/users/${userId}/notification`, {}).then((response) => response.data);
  }
};

export const useNotifications = (options?: { onSuccess?: (data: Notification | null) => void }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { startPolling } = usePostCallPolling(user?.caller_role_id);

  // long poll
  return useQuery(['poll', user?.id], () => fetchNotifications(user?.id), {
    retry: true,
    retryDelay: 1000 * 30,
    enabled: user !== undefined,
    onSuccess: (data) => {
      options?.onSuccess?.(data);
      if (
        data &&
        typeof data === 'object' &&
        data.context === 'fi_search_progress' &&
        typeof data.message === 'string'
      ) {
        window.dispatchEvent(
          new CustomEvent<FiSearchProgressPayload>(FI_SEARCH_PROGRESS_EVENT, {
            detail: data as FiSearchProgressPayload,
          }),
        );
      }
      queryClient.invalidateQueries(['activeCalls']);
      queryClient.invalidateQueries(['callRequests']);
      queryClient.invalidateQueries(['callRequests']);
      // Always invalidate postcall list to ensure "How'd it go?" button appears after call ends
      queryClient.invalidateQueries(['postCallList']);
      if (data?.context === 'engagement_check_in') queryClient.invalidateQueries(['reflections']);
      if (data?.context === 'call_passed' || data?.context === 'updated_offers') {
        queryClient.invalidateQueries(['callRequests']);
      }
      if (data?.context === 'updated_call') {
        // Start polling for postcall metadata
        // postcall_metadata is created asynchronously via fire_and_forget in settle_call,
        // so we poll every 15 seconds until it's ready or 5 minutes have elapsed
        startPolling();
      }
      queryClient.invalidateQueries(['poll']);
    },
  });
};
